import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BracketView from "../knox/BracketView";
import {
  GENERAL_ELECTION_LABEL,
  TENNESSEE_COUNTIES,
  getConfiguredCountyCount,
  getCounty,
  getCountyElection,
} from "../../data/elections/tnAugust62026";
import {
  WHO_FUNDS_TN_SOURCE,
  getWhoFundsCandidateSummary,
  getWhoFundsRaisedTotal,
} from "../../data/elections/whoFundsTn";

const getDaysUntilLabel = (dateString) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(`${dateString}T00:00:00`);
  const targetDay = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const days = Math.round((targetDay - today) / 86400000);

  if (days > 1) return `${days} days until election day`;
  if (days === 1) return "Election day is tomorrow";
  if (days === 0) return "Election day is today";
  return "Election day has passed";
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return "Not timestamped";

  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateString;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const CountySelector = ({ activeSlug, onSelect, sourceUrl }) => {
  const activeCounty = TENNESSEE_COUNTIES.find((county) => county.slug === activeSlug) || null;
  const [query, setQuery] = useState(activeCounty?.name || "");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  useEffect(() => {
    setQuery(activeCounty?.name || "");
    setHighlightedIndex(0);
  }, [activeCounty?.name]);

  const filteredCounties = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return TENNESSEE_COUNTIES;

    return TENNESSEE_COUNTIES.filter((county) =>
      county.name.toLowerCase().includes(needle) ||
      county.slug.toLowerCase().includes(needle)
    );
  }, [query]);

  const chooseCounty = (county) => {
    setQuery(county.name);
    setIsOpen(false);
    setHighlightedIndex(0);
    onSelect(county.slug);
  };

  const handleKeyDown = (event) => {
    if (!isOpen && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      setIsOpen(true);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.min(index + 1, filteredCounties.length - 1));
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
    }

    if (event.key === "Enter" && filteredCounties[highlightedIndex]) {
      event.preventDefault();
      chooseCounty(filteredCounties[highlightedIndex]);
    }

    if (event.key === "Escape") {
      setIsOpen(false);
      setQuery(activeCounty?.name || "");
    }
  };

  return (
    <div className="election-county-select">
      <div className="county-select-label-row">
        <label htmlFor="county-search">County</label>
        {sourceUrl && (
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
            Official source
          </a>
        )}
      </div>
      <div className="county-combobox">
        <input
          id="county-search"
          type="search"
          value={query}
          placeholder="Search counties"
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls="county-search-options"
          aria-autocomplete="list"
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
            setHighlightedIndex(0);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => window.setTimeout(() => setIsOpen(false), 120)}
          onKeyDown={handleKeyDown}
        />

        {isOpen && (
          <div id="county-search-options" className="county-combobox-options" role="listbox">
            {filteredCounties.length > 0 ? (
              filteredCounties.map((county, index) => (
                <button
                  key={county.slug}
                  type="button"
                  role="option"
                  aria-selected={county.slug === activeSlug}
                  className={index === highlightedIndex ? "highlighted" : ""}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    chooseCounty(county);
                  }}
                >
                  <span>{county.name}</span>
                  <small>{county.status === "ready" ? "Archive" : "Official"}</small>
                </button>
              ))
            ) : (
              <div className="county-combobox-empty">No county found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ElectionHero = ({ county, election, onCountySelect }) => {
  const title = election?.title || `${county.name} Election Hub`;
  const statusLabel = election?.resultsStatusLabel || "County shell";
  const dateLabel = election?.electionLabel || GENERAL_ELECTION_LABEL;
  const daysLabel = getDaysUntilLabel(election?.electionDate || "2026-08-06");

  return (
    <section className="election-hero">
      <div className="election-hero-copy">
        <p className="election-kicker">Tennessee local elections</p>
        <h1>{title}</h1>
        <p>
          Track the August 6 county general election, state and federal
          primaries, plain-language office explainers, candidate links, and a
          direct path back to official voter resources.
        </p>
        <div className="election-hero-badges" aria-label="Election status">
          <span>{dateLabel}</span>
          <span>{daysLabel}</span>
          <span>{statusLabel}</span>
        </div>
      </div>

      <div className="election-hero-tools">
        <CountySelector
          activeSlug={county.slug}
          onSelect={onCountySelect}
          sourceUrl={election?.sourceUrl || county.resultSourceUrl}
        />
      </div>
    </section>
  );
};

const ElectionTrustPanel = ({ election }) => {
  const sourceLinks = election?.sourceLinks || [];
  const correctionEmail = election?.correctionEmail || "legislature@mail.tnbills.com";
  const correctionSubject = encodeURIComponent(`${election?.countyName || "Election"} guide correction`);
  const correctionHref = `mailto:${correctionEmail}?subject=${correctionSubject}`;

  return (
    <section className="election-trust-panel" aria-label="Election guide sourcing and corrections">
      <article className="election-trust-card election-trust-card-primary">
        <div className="election-section-heading">
          <p>Source check</p>
          <h2>Archived source information</h2>
        </div>
        <p>
          Candidate names, race groupings, and voter resources are built from
          {` ${election?.countyName || "county"} sample ballot data`} and official
          Tennessee election sources. Confirm your personal ballot and polling
          details before voting.
        </p>
        <div className="election-source-actions">
          {sourceLinks.map((link) => (
            <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer">
              {link.label}
            </a>
          ))}
          <a className="secondary" href={correctionHref}>
            Report correction
          </a>
        </div>
      </article>

      <article className="election-trust-card">
        <div className="election-section-heading">
          <p>Launch status</p>
          <h2>Detailed county coverage</h2>
        </div>
        <ul className="election-trust-list">
          <li>
            <strong>Last checked</strong>
            <span>{formatDisplayDate(election?.lastUpdated)}</span>
          </li>
          <li>
            <strong>Election day</strong>
            <span>{election?.electionLabel || GENERAL_ELECTION_LABEL}</span>
          </li>
          <li>
            <strong>Results</strong>
            <span>{election?.resultsStatusLabel || "Pending"}</span>
          </li>
        </ul>
      </article>
    </section>
  );
};

const BracketControls = ({ filter, setFilter, topParty, setTopParty }) => (
  <div className="election-bracket-controls">
    <div className="btn-group">
      <button
        type="button"
        onClick={() => setFilter("both")}
        className={`btn btn-sm ${filter === "both" ? "btn-dark" : "btn-outline-secondary"}`}
      >
        Both
      </button>
      <button
        type="button"
        onClick={() => setFilter("R")}
        className={`btn btn-sm ${filter === "R" ? "btn-danger" : "btn-outline-danger"}`}
      >
        Republican only
      </button>
      <button
        type="button"
        onClick={() => setFilter("D")}
        className={`btn btn-sm ${filter === "D" ? "btn-primary" : "btn-outline-primary"}`}
      >
        Democrat only
      </button>
    </div>

    <button
      type="button"
      className="btn btn-sm btn-outline-secondary"
      onClick={() => setTopParty((party) => (party === "R" ? "D" : "R"))}
    >
      {topParty === "R" ? "R first" : "D first"}
    </button>
  </div>
);

const PARTY_CONFIG = {
  R: {
    label: "Republican",
    key: "republican",
    color: "#dc3545",
  },
  D: {
    label: "Democratic",
    key: "democrat",
    color: "#0d6efd",
  },
};

const getVisibleParties = (filter) => {
  if (filter === "R") return ["R"];
  if (filter === "D") return ["D"];
  return ["R", "D"];
};

const getOrderedVisibleParties = (filter, topParty) => {
  const visibleParties = getVisibleParties(filter);
  if (visibleParties.length < 2) return visibleParties;
  return topParty === "D" ? ["D", "R"] : ["R", "D"];
};

const getInitials = (name = "") =>
  name
    .replace(/["']/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const formatCompactMoney = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "n/a";

  const amount = Number(value);
  const absoluteAmount = Math.abs(amount);

  if (absoluteAmount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (absoluteAmount >= 1000) return `$${Math.round(amount / 1000).toLocaleString()}k`;

  return `$${Math.round(amount).toLocaleString()}`;
};

const formatCompactCount = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "0";

  const count = Number(value);
  if (Math.abs(count) >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return count.toLocaleString();
};

const PrimaryCandidateAvatar = ({ candidate, party }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const config = PARTY_CONFIG[party];
  const color = config?.color || "#6c757d";

  return (
    <div className="federal-primary-avatar" style={{ borderColor: color, color }}>
      {candidate.image_url && !imageFailed ? (
        <img
          src={candidate.image_url}
          alt=""
          style={{ objectPosition: candidate.image_position || "50% 35%" }}
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span>{getInitials(candidate.name)}</span>
      )}
    </div>
  );
};

const CandidateFinanceSummary = ({ summary }) => {
  if (!summary) return null;

  const hasFilings = Number(summary.filing_count) > 0;
  const raised = getWhoFundsRaisedTotal(summary);

  return (
    <div className="candidate-finance-summary">
      <div className="candidate-finance-heading">
        <span>Finance</span>
        {summary.is_incumbent && <strong>Incumbent</strong>}
      </div>

      {hasFilings ? (
        <div className="candidate-finance-grid">
          <div>
            <span>Raised</span>
            <strong>{formatCompactMoney(raised)}</strong>
          </div>
          <div>
            <span>Spent</span>
            <strong>{formatCompactMoney(summary.total_spent)}</strong>
          </div>
          <div>
            <span>Cash</span>
            <strong>{formatCompactMoney(summary.cash_on_hand)}</strong>
          </div>
          <div>
            <span>Donors</span>
            <strong>{formatCompactCount(summary.contributor_count)}</strong>
          </div>
        </div>
      ) : (
        <p>No filings yet</p>
      )}

      <a href={WHO_FUNDS_TN_SOURCE.bulkDataUrl} target="_blank" rel="noopener noreferrer">
        via WhoFundsTN
      </a>
    </div>
  );
};

const FederalPrimaryCandidateCard = ({ candidate, party, race }) => {
  const config = PARTY_CONFIG[party];
  const financeSummary = getWhoFundsCandidateSummary(candidate.name, race.title);

  return (
    <div className="federal-primary-candidate-card">
      <PrimaryCandidateAvatar candidate={candidate} party={party} />
      <div className="federal-primary-candidate-copy">
        <strong>{candidate.name}</strong>
        <span>{config.label} primary candidate</span>
        {candidate.website_url && (
          <a href={candidate.website_url} target="_blank" rel="noopener noreferrer">
            Website
          </a>
        )}
        <CandidateFinanceSummary summary={financeSummary} />
      </div>
    </div>
  );
};

const FederalPrimaryPartyLane = ({ race, party }) => {
  const config = PARTY_CONFIG[party];
  const candidates = race[config.key] || [];

  return (
    <div
      className="federal-primary-party-lane"
      style={{
        "--party-color": config.color,
        "--party-surface": `${config.color}0a`,
        "--party-border": `${config.color}40`,
      }}
    >
      <div className="federal-primary-lane-header">
        <span>{party}</span>
        <strong>{config.label} primary</strong>
        <small>{candidates.length === 1 ? "1 candidate" : `${candidates.length} candidates`}</small>
      </div>

      {candidates.length > 0 ? (
        <div className="federal-primary-candidate-list">
          {candidates.map((candidate) => (
            <FederalPrimaryCandidateCard key={candidate.id} candidate={candidate} party={party} race={race} />
          ))}
        </div>
      ) : (
        <p className="primary-empty-note">
          No candidate qualified on sample ballot.
        </p>
      )}
    </div>
  );
};

const FederalPrimaryRaceCard = ({ race, filter, topParty }) => {
  const visibleParties = getOrderedVisibleParties(filter, topParty);

  return (
    <article className="primary-race-card federal-primary-bracket">
      <div className="primary-race-header">
        <div>
          <p>{race.scope}</p>
          <h3>{race.title}</h3>
        </div>
        <span>Primary bracket</span>
      </div>

      <p className="primary-district-note">{race.districtNote}</p>

      <div className={`federal-primary-matchup ${visibleParties.length === 1 ? "single-party" : ""}`}>
        {visibleParties.map((party, index) => (
          <React.Fragment key={`${race.id}-${party}`}>
            {index > 0 && (
              <div className="federal-primary-vs" aria-hidden="true">
                vs
              </div>
            )}
            <FederalPrimaryPartyLane race={race} party={party} />
          </React.Fragment>
        ))}
      </div>

      {race.explainer && (
        <div className="primary-explainer">
          <div><strong>Office:</strong> {race.explainer.role}</div>
          <div><strong>Why it matters:</strong> {race.explainer.voterImpact}</div>
        </div>
      )}
    </article>
  );
};

const PrimaryCandidateRow = ({ candidate, color }) => (
  <div className="primary-candidate-row">
    <div>
      <strong>{candidate.name}</strong>
      {candidate.description && <small>{candidate.description}</small>}
    </div>
    <span style={{ color }}>
      {candidate.primary_votes == null ? "Pending" : candidate.primary_votes.toLocaleString()}
    </span>
  </div>
);

const PrimaryPartyPanel = ({ race, party }) => {
  const config = PARTY_CONFIG[party];
  const candidates = race[config.key] || [];

  return (
    <div className="primary-party-panel" style={{ borderColor: `${config.color}40`, background: `${config.color}0a` }}>
      <div className="primary-party-heading" style={{ color: config.color }}>
        {config.label} primary
      </div>

      {candidates.length > 0 ? (
        <div className="primary-candidate-list">
          {candidates.map((candidate) => (
            <PrimaryCandidateRow key={candidate.id} candidate={candidate} color={config.color} />
          ))}
        </div>
      ) : (
        <p className="primary-empty-note">
          No candidate qualified on sample ballot.
        </p>
      )}
    </div>
  );
};

const PrimaryRaceCard = ({ race, filter }) => (
  <article className="primary-race-card">
    <div className="primary-race-header">
      <div>
        <p>{race.scope}</p>
        <h3>{race.title}</h3>
      </div>
      <span>Primary</span>
    </div>

    <p className="primary-district-note">{race.districtNote}</p>

    <div className="primary-party-grid">
      {getVisibleParties(filter).map((party) => (
        <PrimaryPartyPanel key={`${race.id}-${party}`} race={race} party={party} />
      ))}
    </div>

    {race.explainer && (
      <div className="primary-explainer">
        <div><strong>Office:</strong> {race.explainer.role}</div>
        <div><strong>Why it matters:</strong> {race.explainer.voterImpact}</div>
      </div>
    )}
  </article>
);

const getExecutiveDistrict = (title = "") => title.match(/District\s+(\d+)/)?.[1] || "Other";

const getExecutiveOfficeLabel = (title = "") =>
  title
    .replace(/^State Executive\s+/, "")
    .replace(/\s+District\s+\d+$/, "");

const ExecutiveCandidatePill = ({ candidate, party }) => {
  const config = PARTY_CONFIG[party];

  return (
    <div className="executive-candidate-pill">
      <span
        className="executive-candidate-initials"
        style={{ background: config.color }}
      >
        {getInitials(candidate.name)}
      </span>
      <strong>{candidate.name}</strong>
    </div>
  );
};

const ExecutivePartyColumn = ({ race, party }) => {
  const config = PARTY_CONFIG[party];
  const candidates = race[config.key] || [];

  return (
    <div
      className="executive-party-column"
      style={{
        "--party-color": config.color,
        "--party-surface": `${config.color}0a`,
        "--party-border": `${config.color}35`,
      }}
    >
      <div className="executive-party-column-header">
        <span>{party}</span>
        <strong>{config.label}</strong>
      </div>

      {candidates.length > 0 ? (
        <div className="executive-candidate-list">
          {candidates.map((candidate) => (
            <ExecutiveCandidatePill key={candidate.id} candidate={candidate} party={party} />
          ))}
        </div>
      ) : (
        <p className="primary-empty-note">No candidate qualified.</p>
      )}
    </div>
  );
};

const ExecutiveOfficeRow = ({ race, filter, topParty }) => {
  const visibleParties = getOrderedVisibleParties(filter, topParty);

  return (
    <div className="executive-office-row">
      <div className="executive-office-title">
        <strong>{getExecutiveOfficeLabel(race.title)}</strong>
      </div>

      <div className={`executive-office-parties ${visibleParties.length === 1 ? "single-party" : ""}`}>
        {visibleParties.map((party) => (
          <ExecutivePartyColumn key={`${race.id}-${party}`} race={race} party={party} />
        ))}
      </div>
    </div>
  );
};

const ExecutiveCommitteeSection = ({ races, filter, topParty }) => {
  const districts = races.reduce((accumulator, race) => {
    const district = getExecutiveDistrict(race.title);
    return {
      ...accumulator,
      [district]: [...(accumulator[district] || []), race],
    };
  }, {});

  const sortedDistricts = Object.entries(districts).sort(([a], [b]) => Number(a) - Number(b));

  return (
    <div className="executive-committee-grid">
      {sortedDistricts.map(([district, districtRaces]) => (
        <article key={district} className="executive-district-card">
          <div className="executive-district-header">
            <div>
              <p>Executive committee district</p>
              <h4>District {district}</h4>
            </div>
            <span>{districtRaces.length} offices</span>
          </div>

          <div className="executive-office-list">
            {districtRaces.map((race) => (
              <ExecutiveOfficeRow
                key={race.id}
                race={race}
                filter={filter}
                topParty={topParty}
              />
            ))}
          </div>
        </article>
      ))}
    </div>
  );
};

const PrimaryRaceSection = ({ races = [], filter, topParty, countyName }) => {
  if (!races.length) return null;

  const federalRaces = races.filter((race) => race.category === "federal_primary");
  const stateRaces = races.filter((race) => race.category === "state_primary");
  const partyRaces = races.filter((race) => race.category === "party_primary");
  const groups = [
    { id: "federal", title: "Federal Primaries", races: federalRaces },
    { id: "state", title: "State Primaries", races: stateRaces },
    { id: "party", title: "State Executive Committee", races: partyRaces },
  ].filter((group) => group.races.length > 0);

  return (
    <section className="election-guide-view">
      <div className="election-section-heading">
        <p>State & federal primary elections</p>
        <h2>Party primaries</h2>
        <span>Candidate names are loaded from the {countyName} sample ballots.</span>
      </div>

      {groups.map((group) => (
        <div key={group.id} className="primary-race-group">
          <h3>{group.title}</h3>
          {group.id === "party" ? (
            <ExecutiveCommitteeSection races={group.races} filter={filter} topParty={topParty} />
          ) : group.id === "federal" || group.id === "state" ? (
            <div className={`federal-primary-grid ${group.id === "state" ? "state-primary-grid" : ""}`}>
              {group.races.map((race) => (
                <FederalPrimaryRaceCard
                  key={race.id}
                  race={race}
                  filter={filter}
                  topParty={topParty}
                />
              ))}
            </div>
          ) : (
            <div className="primary-race-grid">
              {group.races.map((race) => (
                <PrimaryRaceCard key={race.id} race={race} filter={filter} />
              ))}
            </div>
          )}
        </div>
      ))}
    </section>
  );
};

const StatewidePanel = ({ activeSlug, onSelect }) => {
  const configuredCount = getConfiguredCountyCount();
  const allCountiesLive = configuredCount === TENNESSEE_COUNTIES.length;
  const configuredCounties = TENNESSEE_COUNTIES
    .filter((county) => county.status === "ready")
    .map((county) => county.name.replace(" County", ""));
  const configuredNames = new Intl.ListFormat("en", {
    style: "long",
    type: "conjunction",
  }).format(configuredCounties);
  const availabilityHeading = allCountiesLive
    ? `Archived guides for all ${configuredCount} Tennessee counties`
    : `Archived guides for ${configuredNames}`;

  return (
    <section className="election-statewide-panel">
      <div className="election-section-heading">
        <p>County availability</p>
        <h2>{availabilityHeading}</h2>
        <span>
          {configuredCount} of {TENNESSEE_COUNTIES.length} counties have detailed race data.
        </span>
      </div>

      <p className="election-statewide-copy">
        {allCountiesLive
          ? "Choose your county to see its complete August 6 ballot guide, candidates, primary results, and official election sources."
          : `Detailed guides are available for ${configuredNames}. Other county buttons route voters to official information until detailed race data is imported.`}
      </p>

      <div className="election-county-grid">
        {TENNESSEE_COUNTIES.map((county) => {
          const isConfigured = county.status === "ready";
          const isActive = county.slug === activeSlug;

          return (
            <button
              key={county.slug}
              type="button"
              className={isActive ? "active" : ""}
              onClick={() => onSelect(county.slug)}
            >
              <strong>{county.name}</strong>
              <span className={isConfigured ? "ready" : "shell"}>
                {isConfigured ? "Archive" : "Official"}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const CountyShell = ({ county, onCountySelect }) => (
  <div className="election-content-grid">
    <section className="election-main-column">
      <article className="election-race-card">
        <div className="election-race-header">
          <div>
            <p>Official source page</p>
            <h3>Use official sources for {county.name}</h3>
          </div>
          <span className="election-race-status">Official link</span>
        </div>
        <p className="election-race-explainer">
          Detailed race data is temporarily unavailable for {county.name}. Use the
          official voter lookup and election office resources before making voting plans.
        </p>
        <div className="election-link-list">
          <a href={county.electionCommissionUrl} target="_blank" rel="noopener noreferrer">
            Open official voter information
          </a>
        </div>
      </article>
    </section>
    <ReadinessShell />
    <StatewidePanel activeSlug={county.slug} onSelect={onCountySelect} />
  </div>
);

const ReadinessShell = () => (
  <aside className="election-side-panel">
    <section>
      <h2>County Status</h2>
      <ul className="election-checklist">
        <li>All 95 Tennessee counties have detailed candidate guides.</li>
        <li>This page currently uses official external sources as a fallback.</li>
        <li>No unofficial race data or vote totals are shown here.</li>
      </ul>
    </section>
  </aside>
);

const ArchiveNotice = () => (
  <div className="november-archive-notice">
    <strong>August 6, 2026 archive</strong>
    <p>These are historical candidate guides and pre-election snapshots, not certified August results or November ballots. Linked external pages may have changed since the archive was created.</p>
    <Link to="/">Open the November 3 midterm guide →</Link>
  </div>
);

export const StatewideElectionLanding = ({ onCountySelect }) => (
  <main className="election-page election-landing-page">
    <ArchiveNotice />
    <section className="election-hero election-landing-hero">
      <div className="election-hero-copy">
        <p className="election-kicker">Tennessee local elections</p>
        <h1>Find your Tennessee county election guide</h1>
        <p>
          Explore the August 6 county general election and state and federal
          primaries for all 95 counties. Select your county to see the races and
          candidates that may appear on your ballot.
        </p>
        <div className="election-hero-badges" aria-label="Election coverage">
          <span>{GENERAL_ELECTION_LABEL}</span>
          <span>{getDaysUntilLabel("2026-08-06")}</span>
          <span>All 95 counties archived</span>
        </div>
      </div>

      <div className="election-hero-tools">
        <div className="election-landing-search-copy">
          <strong>Start with your county</strong>
          <span>Search by county name or choose from the full list below.</span>
        </div>
        <CountySelector activeSlug={null} onSelect={onCountySelect} />
      </div>
    </section>

    <StatewidePanel activeSlug={null} onSelect={onCountySelect} />
  </main>
);

const CountyElectionPage = ({ defaultCounty = null }) => {
  const navigate = useNavigate();
  const { countySlug } = useParams();
  const activeSlug = countySlug || defaultCounty;
  const activeCounty = activeSlug ? getCounty(activeSlug) : null;
  const election = activeCounty ? getCountyElection(activeCounty.slug) : null;
  const [topParty, setTopParty] = useState("R");
  const [filter, setFilter] = useState("both");

  useEffect(() => {
    const pageTitle = election?.title
      || (activeCounty ? `${activeCounty.name} Election Hub` : "Tennessee County Election Guides");
    document.title = `August 2026 Archive: ${pageTitle} | TN Bill-E`;
  }, [activeCounty, election?.title]);

  const handleCountySelect = (slug) => {
    navigate(`/elections/tn/august-2026/${slug}`);
  };

  if (!activeCounty) {
    return <StatewideElectionLanding onCountySelect={handleCountySelect} />;
  }

  return (
    <main className="election-page">
      <ArchiveNotice />
      <ElectionHero
        county={activeCounty}
        election={election}
        onCountySelect={handleCountySelect}
      />

      {election ? (
        <>
          <ElectionTrustPanel election={election} />
          <div className="election-content-grid">
            <PrimaryRaceSection
              races={election.primaryRaces}
              filter={filter}
              topParty={topParty}
              countyName={election.countyName}
            />
            <section className="election-guide-view">
              <div className="election-section-heading">
                <p>County general election</p>
                <h2>August 6 matchups</h2>
                <span>Primary losers are hidden so the page stays focused on the general election.</span>
              </div>
              <BracketControls
                filter={filter}
                setFilter={setFilter}
                topParty={topParty}
                setTopParty={setTopParty}
              />
              <BracketView races={election.races} topParty={topParty} filter={filter} />
            </section>
            <StatewidePanel activeSlug={activeCounty.slug} onSelect={handleCountySelect} />
          </div>
        </>
      ) : (
        <CountyShell county={activeCounty} onCountySelect={handleCountySelect} />
      )}
    </main>
  );
};

export default CountyElectionPage;
